import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Search, Mail, Calendar, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface Student {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  created_at: string;
  credits_balance?: number;
  total_sessions?: number;
}

export const StudentManager = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    const filtered = students.filter(student => 
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStudents(filtered);
  }, [students, searchTerm]);

  const fetchStudents = async () => {
    try {
      // Get all student profiles with credit and session data
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select(`
          user_id,
          email,
          first_name,
          last_name,
          is_active,
          created_at
        `)
        .eq('role', 'student')
        .order('created_at', { ascending: false });

      if (profileError) throw profileError;

      // Get credit balances for each student
      const studentsWithData = await Promise.all(
        (profiles || []).map(async (profile) => {
          // Get credit balance
          const { data: credits } = await supabase
            .from('student_credits')
            .select('balance')
            .eq('user_id', profile.user_id)
            .single();

          // Get session count
          const { data: sessions } = await supabase
            .from('sessions')
            .select('id')
            .eq('student_id', profile.user_id);

          return {
            ...profile,
            credits_balance: credits?.balance || 0,
            total_sessions: sessions?.length || 0
          };
        })
      );

      setStudents(studentsWithData);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({
        title: "Error",
        description: "Failed to fetch student data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = (studentId: string) => {
    setStudentToDelete(studentId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteStudent = async () => {
    if (!studentToDelete) return;

    try {
      // Delete the user account (this will cascade to profiles and other tables)
      const { error } = await supabase.auth.admin.deleteUser(studentToDelete);
      
      if (error) throw error;

      toast({
        title: "Success",
        description: "Student account deleted successfully.",
      });

      // Refresh the list
      fetchStudents();
    } catch (error: any) {
      console.error('Error deleting student:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete student account.",
        variant: "destructive",
      });
    } finally {
      setShowDeleteDialog(false);
      setStudentToDelete(null);
    }
  };

  const toggleStudentStatus = async (studentId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: !currentStatus })
        .eq('user_id', studentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Student ${!currentStatus ? 'activated' : 'deactivated'} successfully.`,
      });

      // Refresh the list
      fetchStudents();
    } catch (error: any) {
      console.error('Error updating student status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update student status.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card className="obsidian-card">
        <CardHeader>
          <CardTitle className="text-energy flex items-center gap-2">
            <Users className="h-5 w-5" />
            Student Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Loading students...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="obsidian-card">
        <CardHeader>
          <CardTitle className="text-energy flex items-center gap-2">
            <Users className="h-5 w-5" />
            Student Management
          </CardTitle>
          <div className="space-y-2">
            <Label htmlFor="student-search">Search Students</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                id="student-search"
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredStudents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'No students found matching your search.' : 'No students registered yet'}
              </div>
            ) : (
              <>
                <div className="text-sm text-muted-foreground mb-4">
                  Showing {filteredStudents.length} of {students.length} students
                </div>
                {filteredStudents.map((student) => (
                  <div key={student.user_id} className="p-4 border border-border rounded-lg space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between sm:block">
                          <h4 className="font-medium">{student.first_name} {student.last_name}</h4>
                          <Badge 
                            variant={student.is_active ? 'default' : 'secondary'}
                            className="sm:hidden"
                          >
                            {student.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {student.email}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          Joined {new Date(student.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="hidden sm:block flex-shrink-0">
                        <Badge variant={student.is_active ? 'default' : 'secondary'}>
                          {student.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Credits:</span>
                        <span className="ml-2 font-medium">{student.credits_balance || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sessions:</span>
                        <span className="ml-2 font-medium">{student.total_sessions || 0}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => toggleStudentStatus(student.user_id, student.is_active)}
                        className="flex-1 sm:flex-none"
                      >
                        {student.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDeleteStudent(student.user_id)}
                        className="flex-1 sm:flex-none"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this student account? This action cannot be undone and will remove all associated data including sessions, credits, and profile information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStudent} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};